import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { PlatformUser } from '@nucleus-commerce/api-client/platform';

import { canManageUsers } from '@/shared/session/roles';
import { session } from '@/shared/session/session';

import { CreateUserDialog } from '../components/create-user-dialog';
import { EditUserDialog } from '../components/edit-user-dialog';
import { useUserColumns } from '../components/user-columns';
import { userFilters } from '../components/user-filters';
import { useUserRowActions, type UserRowAction } from '../components/user-row-actions';
import { UserStatusDialog, type UserStatusAction } from '../components/user-status-dialog';
import { useResendWelcome } from '../hooks/use-resend-welcome';
import { useUsers } from '../hooks/use-users';
import type { Actor } from '../schemas/user-rules';

const EMPTY: readonly PlatformUser[] = [];

function UsersList() {
  const { t } = useTranslation('users');
  const { me } = session.useSession();
  const actorId = me?.user.id;
  const actorRole = me?.user.role;
  // Column defs must stay referentially stable: flexRender treats a new `cell` function as a new
  // component type and remounts every cell, which closes any open row menu.
  const actor = useMemo<Actor | null>(
    () =>
      actorId !== undefined && actorRole !== undefined && canManageUsers(actorRole)
        ? { id: actorId, role: actorRole }
        : null,
    [actorId, actorRole],
  );
  const query = useUsers();
  const users = query.data?.items ?? EMPTY;
  const resend = useResendWelcome();
  const resendWelcome = resend.mutate;
  const [editing, setEditing] = useState<PlatformUser | null>(null);
  const [statusChange, setStatusChange] = useState<{
    action: UserStatusAction;
    user: PlatformUser;
  } | null>(null);

  const onAction = useCallback(
    (action: UserRowAction, user: PlatformUser) => {
      switch (action) {
        case 'edit':
          setEditing(user);
          return;
        case 'resend':
          resendWelcome(user.id);
          return;
        case 'disable':
        case 'enable':
          setStatusChange({ action, user });
          return;
      }
    },
    [resendWelcome],
  );

  const columns = useUserColumns({ actor });
  const rowActions = useUserRowActions({ actor, users, onAction });
  const filters = useMemo(() => userFilters(t), [t]);
  const table = useDataTable({
    data: users,
    columns,
    filters,
    getRowId: (row) => row.id,
  });

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{t('title')}</span>}
        description={t('description')}
        actions={actor ? <CreateUserDialog actorRole={actor.role} /> : undefined}
      />
      <DataTable
        table={table}
        search={{ label: t('search'), placeholder: t('search') }}
        rowActions={rowActions}
        getRowLabel={(row) => row.name}
        resourceName={{ singular: t('resource_one'), plural: t('resource_other') }}
        loading={query.isPending}
        fetching={query.isFetching || resend.isPending}
        error={query.error}
        onRetry={() => void query.refetch()}
        tour={{ table: 'platform.users.table', search: 'platform.users.search' }}
        emptyState={
          table.isFiltered
            ? undefined
            : { title: t('empty.title'), description: t('empty.description') }
        }
      />
      {actor ? (
        <>
          <EditUserDialog
            actor={actor}
            user={editing}
            users={users}
            onClose={() => setEditing(null)}
          />
          <UserStatusDialog pending={statusChange} onClose={() => setStatusChange(null)} />
        </>
      ) : null}
    </PageContent>
  );
}

export function UsersPage() {
  return (
    <FeatureErrorBoundary name="users.list">
      <UsersList />
    </FeatureErrorBoundary>
  );
}
